import React, { useCallback, useMemo, useState } from 'react'

import { ModalLayout } from '@/components/ui/modal-layout'

export type ConfirmModalProps = {
  title: string
  description: React.ReactNode | string
  icon?: React.ReactNode

  onCancel?: () => void
  cancelText?: string

  onConfirm: () => Promise<void> | void
  confirmText?: string
  confirmVariant?: 'primary' | 'danger'
}

export function ConfirmModal({
  showConfirmModal,
  setShowConfirmModal,
  title,
  description,
  icon,
  onCancel,
  cancelText = 'ยกเลิก',
  onConfirm,
  confirmText = 'ยืนยัน',
  confirmVariant = 'primary',
}: {
  showConfirmModal: boolean
  setShowConfirmModal: React.Dispatch<React.SetStateAction<boolean>>
} & ConfirmModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await onConfirm()
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
      setShowConfirmModal(false)
    }
  }

  return (
    <ModalLayout
      open={showConfirmModal}
      onOpenChange={setShowConfirmModal}
      title={title}
      description={description}
      icon={icon}
      cancelText={cancelText}
      onCancel={onCancel}
      confirmText={confirmText}
      onConfirm={handleConfirm}
      confirmVariant={confirmVariant}
      isLoading={isLoading}
    >
      {/* Confirm modals don't typically have input fields in the body content, they just present the description */}
      <div className="hidden" />
    </ModalLayout>
  )
}

export function useConfirmModal() {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [props, setProps] = useState<ConfirmModalProps | null>(null)

  const ConfirmModalCallback = useCallback(() => {
    return props ? (
      <ConfirmModal
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        {...props}
      />
    ) : null
  }, [showConfirmModal, props])

  const confirm = useCallback(
    (options: ConfirmModalProps) => {
      setProps(options)
      setShowConfirmModal(true)
    },
    [setProps, setShowConfirmModal],
  )

  return useMemo(
    () => ({
      confirm,
      ConfirmModal: ConfirmModalCallback,
    }),
    [confirm, ConfirmModalCallback],
  )
}
